<template>
  <div class="vsm-cards">
    <div v-for="item in items" :key="item.name" class="vsm-cards-card">
      <div class="vsm-cards-head">
        <span class="vsm-cards-vehicle">{{ item.name }}</span>
        <slot name="controls" :item="item"/>
        <span v-if="item.next_mvmt_delta" class="vsm-cards-depart">
          <span class="vsm-cards-depart-delta">{{ item.next_mvmt_delta }}</span>
          <span v-if="departSub(item)" class="vsm-cards-depart-time">{{ departSub(item) }}</span>
        </span>
      </div>
      <div v-if="hasNextInfo(item)" class="vsm-cards-next">
        <span v-if="item.next_mvmt_id" class="vsm-cards-next-id">{{ item.next_mvmt_id }}</span>
        <span v-if="nextDriver(item)">{{ nextDriver(item) }}</span>
        <span v-if="item.next_mvmt_return_time" class="vsm-cards-next-return">Ret {{ fmt(item.next_mvmt_return_time) }}</span>
      </div>
      <!-- vehicles with several upcoming movements list every one, matching
           the table's next_mvmt_list sub-rows -->
      <div v-for="(m, mi) in item.next_mvmt_list || []" :key="'next-' + mi" class="vsm-cards-next vsm-cards-next--extra">
        <span v-if="m.next_mvmt_id" class="vsm-cards-next-id">{{ m.next_mvmt_id }}</span>
        <span v-if="m.next_mvmt_driver">{{ Array.isArray(m.next_mvmt_driver) ? m.next_mvmt_driver.join(', ') : m.next_mvmt_driver }}</span>
        <span v-if="m.next_mvmt_time">{{ fmt(m.next_mvmt_time) }}</span>
        <span v-if="m.next_mvmt_return_time" class="vsm-cards-next-return">Ret {{ fmt(m.next_mvmt_return_time) }}</span>
      </div>
      <!-- the rest of the portal-visible columns, as compact label/value
           pairs (head/next rows and notes already cover their own keys) -->
      <div v-if="fields.length && cardBodyFields(item).length" class="vsm-cards-body">
        <span v-for="f in cardBodyFields(item)" :key="f.key" class="vsm-cards-field">
          <span class="vsm-cards-field-label">{{ f.label || f.key }}</span>{{ fieldValue(item, f) }}
        </span>
      </div>
      <div v-if="item.notes" class="vsm-cards-notes">{{ item.notes }}</div>
      <slot name="body" :item="item"/>
    </div>
    <div v-if="!items.length" class="text-center text-muted py-4">No vehicles found.</div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'vsm_mobile_cards',

  props: {
    items: { type: Array, default: () => [] },
    datetimeFormat: { type: String, default: 'MM/DD hh:mm A' },
    // the page's portal-visible column list; every field not already covered
    // by the card's fixed areas renders as a label/value pair in the body
    fields: { type: Array, default: () => [] },
    // maps next_scheduled_config values to their option labels (pages with
    // the Daily Schedule column pass their scheduled_options list)
    scheduledOptions: { type: Array, default: () => [] },
  },

  methods: {
    cardBodyFields(item) {
      // keys the fixed card areas already show, plus action/pseudo columns
      const covered = [
        'name', 'vehicle_status', 'maintenance_status', 'notes',
        'next_mvmt_delta', 'next_mvmt_time', 'next_mvmt_id',
        'next_mvmt_driver', 'next_mvmt_return_time',
        'details', 'update_status', 'actuals_chart', 'delete', 'reprocess', 'retry',
      ]
      return this.fields.filter(f =>
        f && f.key && !covered.includes(f.key) && this.fieldValue(item, f) !== ''
      )
    },
    fieldValue(item, field) {
      const v = this.rawFieldValue(item, field)
      if (v === true) return 'Yes'
      if (v === false) return 'No'
      if (v == null || v === '') return ''
      if (Array.isArray(v)) return v.join(', ')
      if (/_(time|date)$/.test(field.key) || field.key === 'processed_time') return this.fmt(v)
      return String(v)
    },
    // pseudo-columns the table builds in slots have no direct item key;
    // mirror those slots so the cards don't silently drop the data
    rawFieldValue(item, field) {
      const info = item.vehicle_info || {}
      switch (field.key) {
        case 'vehicle_odo':
          return info.obdOdometer ? Number(info.obdOdometer).toLocaleString() : ''
        case 'vehicle_fuel':
          return (info.fuel_percent || info.fuel_percent === 0) ? info.fuel_percent + '%' : ''
        case 'vehicle_def':
          return (info.def_percent || info.def_percent === 0) ? info.def_percent + '%' : ''
        case 'next_mvmt_start_finish_base':
          return (item.next_mvmt_start_base || item.next_mvmt_finish_base)
            ? (item.next_mvmt_start_base || '') + ' / ' + (item.next_mvmt_finish_base || '')
            : ''
        case 'next_scheduled_config': {
          const opt = this.scheduledOptions.find(o => o && o.value === item.next_scheduled_config)
          return opt ? opt.text : item.next_scheduled_config
        }
        default:
          return item[field.key]
      }
    },
    fmt(value) {
      if (!value)
        return ''
      const m = moment(value)
      return m.isValid() ? m.format(this.datetimeFormat) : String(value)
    },
    departSub(item) {
      return this.fmt(item.next_mvmt_time)
    },
    nextDriver(item) {
      // CM rows carry driver arrays; TBN rows carry strings
      const driver = item.next_mvmt_driver
      if (Array.isArray(driver))
        return driver.join(', ')
      return driver || ''
    },
    hasNextInfo(item) {
      return !!(item.next_mvmt_id || this.nextDriver(item) || item.next_mvmt_return_time)
    },
  },
}
</script>

<style scoped>
.vsm-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.vsm-cards-card {
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 8px 10px;
  background-color: var(--fi-surface, #fff);
  text-align: left;
}
.vsm-cards-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: bold;
  font-size: 15px;
}
.vsm-cards-depart {
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.15;
}
.vsm-cards-depart-delta {
  font-size: 13px;
}
.vsm-cards-depart-time {
  font-size: 11px;
  font-weight: normal;
  color: #6c757d;
}
.vsm-cards-next {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 10px;
  margin-top: 3px;
  font-size: 13px;
}
.vsm-cards-next-id {
  font-weight: 600;
}
.vsm-cards-next-return {
  color: #6c757d;
}
.vsm-cards-next--extra {
  margin-top: 1px;
  font-size: 12px;
  color: #495057;
}
.vsm-cards-body {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 12px;
  margin-top: 4px;
  font-size: 12.5px;
}
.vsm-cards-field { white-space: nowrap; }
.vsm-cards-field-label {
  color: #6c757d;
  font-weight: 600;
  font-size: 11px;
  margin-right: 4px;
}
.vsm-cards-notes {
  margin-top: 4px;
  font-size: 13px;
  color: #6c757d;
}
</style>
